import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SatelliteImage } from 'src/app/tga/shared/models/models';
import { TgaApiService } from 'src/app/tga/shared/services/tga-api.service';
import { blobToBase64 } from 'src/app/tga/shared/utils/blob.utils';

/** How many days to look back when fetching more images */
const FETCH_MORE_DAYS = 30;

function calculateDateRange(beforeDate: Date, minDate: Date): { fromDate: string; toDate: string } {
  const windowStart = new Date(beforeDate);
  windowStart.setDate(windowStart.getDate() - FETCH_MORE_DAYS);

  const effectiveStart = windowStart < minDate ? minDate : windowStart;

  const fromDate = new Date(
    Date.UTC(effectiveStart.getFullYear(), effectiveStart.getMonth(), effectiveStart.getDate(), 0, 0, 0, 0),
  ).toISOString();

  const dayBefore = new Date(beforeDate);
  dayBefore.setDate(dayBefore.getDate() - 1);
  const toDate = new Date(
    Date.UTC(dayBefore.getFullYear(), dayBefore.getMonth(), dayBefore.getDate(), 23, 59, 59, 999),
  ).toISOString();

  return { fromDate, toDate };
}

function createImageRequests(
  tgaApiService: TgaApiService,
  cropFieldId: number,
  dates: Array<{ imageDate: string | Date }>,
): Observable<(SatelliteImage | null)[]> {
  const imageRequests = dates.map((dateObj) => {
    const isoDate =
      typeof dateObj.imageDate === 'string' ? dateObj.imageDate : new Date(dateObj.imageDate).toISOString();

    return tgaApiService.getTgaSatelliteImage(cropFieldId, isoDate).pipe(
      switchMap((blob) => blobToBase64(blob)),
      map(
        (base64): SatelliteImage => ({
          imageDate: isoDate,
          imageBase64: base64,
          contentType: 'image/png',
        }),
      ),
      catchError(() => of(null)),
    );
  });

  return forkJoin(imageRequests);
}

interface TgaSatelliteImagesDialogState {
  images: SatelliteImage[];
  cropFieldId: number;
  fetchedFromDate: string;
  isLoadingMore: boolean;
}

const initialState: TgaSatelliteImagesDialogState = {
  images: [],
  cropFieldId: 0,
  fetchedFromDate: new Date().toISOString(),
  isLoadingMore: false,
};

export const TgaSatelliteImagesDialogStore = signalStore(
  // Not providedIn: 'root' — must be scoped to the dialog component via providers: []
  withState(initialState),
  withComputed((store) => ({
    sortedImages: computed(() =>
      [...store.images()].sort((a, b) => new Date(a.imageDate).getTime() - new Date(b.imageDate).getTime()),
    ),
    oldestImageDate: computed(() => {
      const images = store.images();
      if (images.length === 0) {
        return new Date().toISOString();
      }
      const sorted = [...images].sort((a, b) => new Date(a.imageDate).getTime() - new Date(b.imageDate).getTime());
      return sorted[0].imageDate;
    }),
  })),
  withMethods((store) => {
    const tgaApiService = inject(TgaApiService);

    function initialize(cropFieldId: number, images: SatelliteImage[], fetchedFromDate: string): void {
      patchState(store, { cropFieldId, images, fetchedFromDate });
    }

    function mergeAndPatchImages(results: (SatelliteImage | null)[], fromDate: string): SatelliteImage[] {
      const newImages = results.filter((img): img is SatelliteImage => img !== null);

      // Merge: keep existing images and add only truly new ones (dedup by imageDate key)
      const existingKeys = new Set(store.images().map((img) => img.imageDate));
      const uniqueNewImages = newImages.filter((img) => !existingKeys.has(img.imageDate));

      patchState(store, {
        images: [...store.images(), ...uniqueNewImages],
        isLoadingMore: false,
        fetchedFromDate: fromDate,
      });

      return store.sortedImages();
    }

    /**
     * Fetch images in the window [max(minDate, beforeDate - FETCH_MORE_DAYS), beforeDate].
     * New images are merged into the store; duplicates (by imageDate) are discarded.
     *
     * @returns Observable that resolves with the updated sorted image list once the fetch is done.
     */
    function fetchMoreBefore(beforeDate: Date, minDate: Date): Observable<SatelliteImage[]> {
      const cropFieldId = store.cropFieldId();
      const { fromDate, toDate } = calculateDateRange(beforeDate, minDate);

      if (new Date(fromDate) > new Date(toDate)) {
        return of(store.sortedImages());
      }

      patchState(store, { isLoadingMore: true });

      return tgaApiService.getTgaSatelliteImageDates(cropFieldId, fromDate, toDate).pipe(
        switchMap((dates) => {
          if (!dates || dates.length === 0) {
            patchState(store, { isLoadingMore: false, fetchedFromDate: fromDate });
            return of(store.sortedImages());
          }

          return createImageRequests(tgaApiService, cropFieldId, dates).pipe(
            map((results) => mergeAndPatchImages(results, fromDate)),
          );
        }),
        catchError(() => {
          patchState(store, { isLoadingMore: false });
          return of(store.sortedImages());
        }),
      );
    }

    return { initialize, fetchMoreBefore };
  }),
);

export type TgaSatelliteImagesDialogStoreType = InstanceType<typeof TgaSatelliteImagesDialogStore>;
